import { useCallback, useEffect, useState } from "react";

import { Download, Loader2, MoreVertical, Package, Plus, RotateCcw } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import PageHeader from "../../components/shared/PageHeader";
import Pagination from "../../components/shared/Pagination";
import SearchInput from "../../components/shared/SearchInput";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label.jsx";
import { Switch } from "../../components/ui/switch.jsx";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../components/ui/dropdown-menu";
import { useApi } from "../../context/ApiContext";
import { useAuth } from "../../context/AuthContext";
import { useData } from "../../context/DataContext";
import { useConfirm } from "../../context/ConfirmContext.jsx";
import { usePagination } from "../../hooks/usePagination";
import DocumentViewer from "../Photos/component/DocumentViewer";

import MaterialForm from "./component/MaterialForm";
import MaterialTable from "./component/MaterialTable";

const Materials = () => {
  const { user } = useAuth();
  const { projects, users, globalProjectId } = useData();
  const api = useApi();
  const navigate = useNavigate();

  const [vendors, setVendors] = useState([]);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingMaterialId, setEditingMaterialId] = useState(null);
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [viewerData, setViewerData] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [isExporting, setIsExporting] = useState(false);
  const [selectedVendorId, setSelectedVendorId] = useState("");
  const [sortBy, setSortBy] = useState("createdAt");
  const [direction, setDirection] = useState("desc");
  const [showDeleted, setShowDeleted] = useState(false);

  const openViewer = (data) => {
    setViewerData(data);
    setIsViewerOpen(true);
  };

  const closeViewer = () => {
    setIsViewerOpen(false);
    setViewerData("");
  };

  const pagination = usePagination(10);
  const { items: materials, currentPage: page, pageSize, setPaginationData, setCurrentPage: setPage } = pagination;

  const loadMaterials = useCallback(
    async (pageToLoad) => {
      try {
        const response = await api.get(`/materials`, {
          params: {
            projectId: globalProjectId === "ALL" ? "" : globalProjectId,
            vendorId: selectedVendorId || undefined,
            page: pageToLoad,
            size: pageSize,
            search: searchTerm.trim() || undefined,
            sortBy,
            direction,
            showDeleted
          }
        });
        setPaginationData(response.data);
      } catch (error) {
        console.error("Failed to load materials:", error);
        toast.error(error.message || "Failed to load materials");
      }
    },
    [api, globalProjectId, pageSize, searchTerm, selectedVendorId, setPaginationData, sortBy, direction, showDeleted]
  );

  useEffect(() => {
    loadMaterials(page);
  }, [page, globalProjectId, sortBy, direction, showDeleted, loadMaterials]);

  const loadVendors = async () => {
    try {
      const response = await api.get(`/vendors`);
      console.log("Vendors loaded:", response.data.items);
      setVendors(response.data.items);
    } catch (error) {
      console.error("Failed to load vendors:", error);
      toast.error(error.message || "Failed to load vendors");
    }
  };

  const confirm = useConfirm();

  // Delete material by ID
  const deleteMaterial = async (materialId) => {
    const isConfirmed = await confirm({
      title: "Delete Material",
      message: "Are you sure you want to delete this material?"
    });
    if (!isConfirmed) return;
    try {
      await api.delete(`/materials/${materialId}`);
      toast.success("Material deleted successfully!");
      loadMaterials(page);
    } catch (error) {
      toast.error(error.message || "Failed to delete material");
    }
  };

  const editMaterial = (materialId) => {
    setEditingMaterialId(materialId);
    setIsCreateOpen(true);
  };

  const handleClearAllFilters = () => {
    setSearchTerm("");
    setSelectedVendorId("");
    setSortBy("createdAt");
    setDirection("desc");
    setShowDeleted(false);
    setPage(0);
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const response = await api.get(`/materials/export`, {
        params: {
          projectId: globalProjectId === "ALL" ? "" : globalProjectId,
          vendorId: selectedVendorId || undefined,
          search: searchTerm.trim() || undefined
        },
        responseType: "blob"
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `materials_report_${new Date().getTime()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Export successful!");
    } catch (error) {
      console.error("Export failed:", error);
      toast.error("Failed to export materials");
    } finally {
      setIsExporting(false);
    }
  };

  useEffect(() => {
    loadVendors();
  }, []);

  // loadMaterials dependency handled by useEffect above

  return (
    <>
      <div className="flex-1 flex flex-col animate-in fade-in duration-500">
        <div className="flex-1 space-y-6">
          <PageHeader
            title="Material Management"
            subtitle="Track construction materials and inventory"
            pageIcon={Package}
            extraActions={
              <div className="flex items-center gap-3">
                {(searchTerm || selectedVendorId || sortBy !== "createdAt" || showDeleted) && (
                  <Button
                    variant="ghost"
                    onClick={handleClearAllFilters}
                    className="h-10 px-3 text-slate-500 hover:text-primary hover:bg-slate-100 dark:hover:bg-yale-blue-800/30 flex items-center gap-2"
                  >
                    <RotateCcw className="w-4 h-4" />
                    <span className="hidden sm:inline">Clear All</span>
                  </Button>
                )}
                <SearchInput
                  placeholder="Search materials..."
                  className="md:w-72"
                  value={searchTerm}
                  onSearch={(val) => {
                    setSearchTerm(val);
                    setPage(0);
                  }}
                />
                {user?.permissions?.material?.create && (
                  <Button
                    onClick={() => setIsCreateOpen(true)}
                    className="group relative overflow-hidden bg-primary hover:bg-yale-blue-700 text-white font-bold h-10 w-10 md:h-11 md:w-auto md:px-6 rounded-xl shadow-lg shadow-primary/25 transition-all duration-300 active:scale-95 flex items-center justify-center gap-2"
                    aria-label="Add Material"
                    title="Add Material"
                  >
                    <Plus className="w-4 h-4 transition-transform group-hover:rotate-12" />
                    <span className="hidden md:inline">Add Material</span>
                  </Button>
                )}
                {user?.permissions?.material?.read && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="icon" className="h-10 w-10 border-slate-200 dark:border-yale-blue-800">
                        <MoreVertical className="w-5 h-5" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                      <DropdownMenuItem onClick={handleExport} disabled={isExporting} className="cursor-pointer">
                        {isExporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                        {isExporting ? "Exporting..." : "Export to Excel"}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            }
          />

          <MaterialForm
            isOpen={isCreateOpen}
            setIsOpen={setIsCreateOpen}
            loadMaterials={loadMaterials}
            editingMaterialId={editingMaterialId}
            setEditingMaterialId={setEditingMaterialId}
            selectedProject={globalProjectId === "ALL" ? "" : globalProjectId}
            vendors={vendors}
          />

          <MaterialTable
            materials={materials}
            permissions={{
              canEdit: user?.permissions?.material?.update,
              canDelete: user?.permissions?.material?.delete
            }}
            onEdit={editMaterial}
            onDelete={deleteMaterial}
            onViewPhoto={openViewer}
            filters={{
              vendorId: selectedVendorId,
              sortBy,
              direction
            }}
            showDeleted={showDeleted}
            setShowDeleted={setShowDeleted}
            setPage={setPage}
            onFilterChange={(type, val) => {
              if (type === "sortBy") {
                setSortBy(val);
                setPage(0);
              } else if (type === "direction") {
                setDirection(val);
                setPage(0);
              } else if (type === "vendorId") {
                setSelectedVendorId(val);
                setPage(0);
              }
            }}
            vendorId={selectedVendorId}
            onVendorChange={(val) => {
              setSelectedVendorId(val);
              setPage(0);
            }}
          />
        </div>

        <div className="mt-8">
          <Pagination pagination={pagination} />
        </div>
      </div>
      <DocumentViewer isOpen={isViewerOpen} onClose={closeViewer} data={viewerData} />
    </>
  );
};

export default Materials;
